import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasSessionHint } from '../services/session';
import { resolveLandingPath } from '../lib/landingPath';
import Logo from '../components/Logo';
import SessionCheckScreen from '../components/SessionCheckScreen';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface LocationState {
  from?: {
    pathname: string;
  };
  registrationSuccess?: boolean;
}

const LoginPage: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  
  const { login, isAuthenticated, authReady, serverUnreachable } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState;
  // True from the moment the form is submitted: that login navigates itself.
  const signingIn = useRef(false);
  // Someone who may still be signed in sees a brief check instead of the form.
  const [resuming, setResuming] = useState<boolean>(() => hasSessionHint());

  // Still signed in (bookmark on the start page, browser reopened): go
  // straight to the app instead of asking for the password again.
  useEffect(() => {
    if (!authReady || signingIn.current) return undefined;
    if (!isAuthenticated) {
      setResuming(false);
      return undefined;
    }
    let cancelled = false;
    resolveLandingPath().then((path) => {
      if (!cancelled) navigate(path, { replace: true });
    });
    return () => {
      cancelled = true;
    };
  }, [authReady, isAuthenticated, navigate]);

  useEffect(() => {
    if (locationState?.registrationSuccess) {
      setShowSuccessMessage(true);
    }
  }, [locationState]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    
    if (!formData.email || !formData.password) {
      setError('Bitte füllen Sie alle Felder aus');
      return;
    }

    try {
      setLoading(true);
      signingIn.current = true;
      const success = await login(formData.email, formData.password);
      
      if (success) {
        navigate(await resolveLandingPath(), { replace: true });
      } else {
        signingIn.current = false;
        setError('Ungültige E-Mail oder Passwort');
      }
    } catch (err) {
      signingIn.current = false;
      setError('Ein Fehler ist während des Anmeldevorgangs aufgetreten');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (resuming) {
    return <SessionCheckScreen serverUnreachable={serverUnreachable} />;
  }

  return (
    <div className="min-h-screen flex flex-col lg:grid lg:grid-cols-2">
      {/* Linke Seite: Branding / Info (Desktop) */}
      <div className="hidden lg:flex flex-col justify-between bg-muted py-12 pr-12 pl-24 relative overflow-hidden">
        <div className="z-10 w-full max-w-lg">
          <Logo />
          <div className="mt-20">
            <h1 className="text-4xl font-bold tracking-tight text-foreground mb-6">
              Automatisieren Sie Ihre Terminbuchung mit KI
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              Nutzen Sie unseren intelligenten Assistenten für WhatsApp und Telefon.
              Sparen Sie Zeit durch vollautomatisierte Terminvergabe rund um die Uhr.
            </p>
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-muted-foreground">
                <CheckCircle2 className="h-5 w-5 text-success-strong" />
                <span>24/7 Terminbuchung für Ihre Kunden</span>
              </div>
              <div className="flex items-center gap-3 text-muted-foreground">
                <CheckCircle2 className="h-5 w-5 text-success-strong" />
                <span>Automatische Erinnerungen & Bestätigungen</span>
              </div>
              <div className="flex items-center gap-3 text-muted-foreground">
                <CheckCircle2 className="h-5 w-5 text-success-strong" />
                <span>Nahtlose Integration in Ihr System</span>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute inset-0 z-0 opacity-5 pointer-events-none">
           <img src="/images/timeglobe.svg" alt="Background" className="w-[150%] h-[150%] -translate-x-1/4 -translate-y-1/4 object-contain" />
        </div>

        <div className="z-10 text-xs text-muted-foreground">
          © {new Date().getFullYear()} EcomTask. Alle Rechte vorbehalten.
        </div>
      </div>

      {/* Rechte Seite: Login Form */}
      <div className="flex-1 flex flex-col justify-center items-center p-6 bg-background relative">
        <div className="w-full max-w-sm mx-auto space-y-8">
          <div className="flex flex-col space-y-2 text-center lg:text-left">
            <div className="lg:hidden flex justify-center mb-6">
              <Logo />
            </div>
            <h2 className="text-3xl font-bold tracking-tight">Willkommen zurück</h2>
            <p className="text-muted-foreground">
              Melden Sie sich an, um auf Ihr Dashboard zuzugreifen
            </p>
          </div>

          {showSuccessMessage && (
             <Alert className="bg-success/10 border-success/25 text-success-strong">
               <CheckCircle2 className="h-4 w-4 text-success-strong" />
               <AlertDescription>
                 Registrierung erfolgreich! Bitte melden Sie sich an.
               </AlertDescription>
             </Alert>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">E-Mail-Adresse</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="name@firma.de"
                required
                value={formData.email}
                onChange={handleChange}
                disabled={loading}
                className="h-11"
              />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Passwort</Label>
                <RouterLink 
                  to="/forgot-password" 
                  className="text-sm font-medium text-primary-strong hover:underline"
                >
                  Vergessen?
                </RouterLink>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  disabled={loading}
                  className="h-11 pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-11 w-11 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-11 text-base bg-[#1967D2] hover:bg-[#1756B6]" 
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Anmelden...
                </>
              ) : (
                'Anmelden'
              )}
            </Button>
          </form>

          <div className="text-center text-sm">
            Noch kein Konto?{' '}
            <RouterLink to="/signup" className="font-medium text-primary-strong hover:underline">
              Jetzt registrieren
            </RouterLink>
          </div>
        </div>

        {/* Footer */}
        <div className="absolute bottom-4 left-0 right-0 flex justify-center items-center opacity-70">
          <span className="text-xs text-muted-foreground mr-2">powered by</span>
          <img src="/images/EcomTask_logo.svg" alt="EcomTask" className="h-6 opacity-60 grayscale hover:grayscale-0 transition-all" />
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
